import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Clock,
  Target,
  Mail,
  Star,
  Heart,
  TrendingUp,
  Gift,
  Users,
  ArrowRight,
  Eye,
  Gem
} from 'lucide-react';
import { useEmailTriggerTemplates, EmailTriggerTemplate } from '@/hooks/email/useEmailTriggers';
import {
  TRIGGER_TYPE_LABELS,
  CONDITION_FIELD_LABELS,
  OPERATOR_LABELS
} from '@/types/email-triggers';
import { formatTimeDelay, getTimingMessage } from '@/utils/email/time-formatting';

interface EmailTriggerTemplatesProps {
  onTemplateSelect: (template?: EmailTriggerTemplate) => void;
  onCreateBlank: () => void;
}

type LoyaltySubtype = 'spend' | 'value-tiers' | 'simple' | 'all';

const EmailTriggerTemplates: React.FC<EmailTriggerTemplatesProps> = ({
  onTemplateSelect,
  onCreateBlank
}) => {
  const { templates, loading } = useEmailTriggerTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTriggerTemplate | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loyaltySubtype, setLoyaltySubtype] = useState<LoyaltySubtype>('all');

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'welcome':
        return <Heart className="h-5 w-5" />;
      case 'review':
        return <Mail className="h-5 w-5" />;
      case 'retention':
        return <TrendingUp className="h-5 w-5" />;
      case 'loyalty':
        return <Gem className="h-5 w-5" />;
      case 'milestone':
        return <Star className="h-5 w-5" />;
      default:
        return <Target className="h-5 w-5" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'welcome':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'review':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'retention':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'loyalty':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'milestone':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatConditions = (template: EmailTriggerTemplate) => {
    return template.default_conditions.map(condition => {
      const fieldLabel = CONDITION_FIELD_LABELS[condition.field] || condition.field;
      const operatorLabel = OPERATOR_LABELS[condition.operator] || condition.operator;

      let valueText = condition.value;
      if (Array.isArray(condition.value)) {
        valueText = condition.value.join(', ');
      }

      return `${fieldLabel} ${operatorLabel} ${valueText}`;
    }).join(` ${template.default_logic} `);
  };

  const getLoyaltySubtype = (template: EmailTriggerTemplate): LoyaltySubtype => {
    if (template.category !== 'loyalty') return 'all';

    // Spend-based: loyalty-spend-tier-X
    if (template.id.startsWith('loyalty-spend-tier')) {
      return 'spend';
    }

    // Value tiers: loyalty-low-value or loyalty-high-value
    if (template.id.includes('low-value') || template.id.includes('high-value')) {
      return 'value-tiers';
    }

    // Simple: loyalty-simple-X
    if (template.id.startsWith('loyalty-simple')) {
      return 'simple';
    }

    return 'all';
  };


  const handleUseTemplate = (template: EmailTriggerTemplate) => {
    // Store template data in localStorage for the form to pick up
    localStorage.setItem('selectedEmailTriggerTemplate', JSON.stringify(template));
    onTemplateSelect(template);
  };

  const handlePreviewTemplate = (template: EmailTriggerTemplate) => {
    setSelectedTemplate(template);
    setPreviewOpen(true);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-full" />
                <div className="h-3 bg-gray-200 rounded w-2/3" />
                <div className="h-8 bg-gray-200 rounded w-20" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Create from scratch option */}
        <Card className="border-dashed border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gray-100 rounded-lg">
                  <Target className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <h3 className="font-medium">Create from Scratch</h3>
                  <p className="text-sm text-muted-foreground">
                    Build a custom email trigger with your own conditions and settings
                  </p>
                </div>
              </div>
              <Button onClick={onCreateBlank}>
                Start Fresh
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Template categories */}
        <div className="space-y-8">
          {['welcome', 'review', 'loyalty', 'retention', 'milestone'].map(category => {
            let categoryTemplates = templates.filter(t => t.category === category);
            if (categoryTemplates.length === 0) return null;

            // Filter loyalty templates by subtype
            if (category === 'loyalty' && loyaltySubtype !== 'all') {
              categoryTemplates = categoryTemplates.filter(t => getLoyaltySubtype(t) === loyaltySubtype);
            }

            return (
              <div key={category}>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-2 rounded-lg ${getCategoryColor(category)}`}>
                    {getCategoryIcon(category)}
                  </div>
                  <h3 className="text-lg font-medium capitalize">{category} Triggers</h3>
                </div>

                {/* Loyalty subtype tabs */}
                {category === 'loyalty' && (
                  <div className="mb-6 border-b border-gray-200">
                    <nav className="flex space-x-8" aria-label="Loyalty types">
                      <button
                        onClick={() => setLoyaltySubtype('all')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                          loyaltySubtype === 'all'
                            ? 'border-amber-500 text-amber-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        All ({templates.filter(t => t.category === 'loyalty').length})
                      </button>
                      <button
                        onClick={() => setLoyaltySubtype('spend')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                          loyaltySubtype === 'spend'
                            ? 'border-amber-500 text-amber-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        Spend-Based ({templates.filter(t => getLoyaltySubtype(t) === 'spend').length})
                      </button>
                      <button
                        onClick={() => setLoyaltySubtype('value-tiers')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                          loyaltySubtype === 'value-tiers'
                            ? 'border-amber-500 text-amber-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        Value Tiers ({templates.filter(t => getLoyaltySubtype(t) === 'value-tiers').length})
                      </button>
                      <button
                        onClick={() => setLoyaltySubtype('simple')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                          loyaltySubtype === 'simple'
                            ? 'border-amber-500 text-amber-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        Simple ({templates.filter(t => getLoyaltySubtype(t) === 'simple').length})
                      </button>
                    </nav>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryTemplates.map(template => (
                    <Card key={template.id} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <CardTitle className="text-base">{template.name}</CardTitle>
                            <Badge variant="outline" className="w-fit">
                              {TRIGGER_TYPE_LABELS[template.trigger_type]}
                            </Badge>
                          </div>
                        </div>
                        <CardDescription className="text-sm">
                          {template.description}
                        </CardDescription>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        {/* Quick stats */}
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <div className="flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatTimeDelay(template.default_time_delay)}
                          </div>
                          <div className="flex items-center">
                            <Target className="h-3 w-3 mr-1" />
                            {template.default_conditions.length} condition{template.default_conditions.length !== 1 ? 's' : ''}
                          </div>
                        </div>

                        {/* Email template reference */}
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs text-muted-foreground mb-1">Email Template:</p>
                          <p className="text-sm font-medium">
                            {template.system_template_name}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1 italic">
                            Email content will be customized in the trigger form
                          </p>
                        </div>

                        {/* Use cases */}
                        <div>
                          <p className="text-xs text-muted-foreground mb-2">Use Cases:</p>
                          <div className="flex flex-wrap gap-1">
                            {template.use_cases.slice(0, 2).map((useCase, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {useCase}
                              </Badge>
                            ))}
                            {template.use_cases.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{template.use_cases.length - 2} more
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            onClick={() => handleUseTemplate(template)}
                            className="flex-1"
                          >
                            Use Template
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePreviewTemplate(template)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Template Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <span>{selectedTemplate?.name}</span>
              <Badge variant="outline">
                {selectedTemplate && TRIGGER_TYPE_LABELS[selectedTemplate.trigger_type]}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate?.description}
            </DialogDescription>
          </DialogHeader>

          {selectedTemplate && (
            <div className="space-y-6">
              {/* Conditions */}
              <div>
                <h4 className="font-medium mb-2">Trigger Conditions</h4>
                <div className="bg-gray-50 rounded p-4">
                  <p className="text-sm font-mono">
                    {formatConditions(selectedTemplate)}
                  </p>
                </div>
              </div>

              {/* Timing */}
              <div>
                <h4 className="font-medium mb-2">Timing</h4>
                <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{
                  __html: getTimingMessage(selectedTemplate).replace(
                    /(immediately|\d+\s+\w+(?:,\s*\d+\s+\w+)?)/g,
                    '<strong>$1</strong>'
                  )
                }} />
              </div>

              {/* Email Template Reference */}
              <div>
                <h4 className="font-medium mb-2">Email Template</h4>
                <div className="bg-gray-50 rounded p-4">
                  <p className="text-sm font-medium mb-2">
                    {selectedTemplate.system_template_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    The trigger will use this email template. You can customize the content when creating the trigger.
                  </p>
                </div>

                {selectedTemplate.personalization_fields.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      Required personalization variables:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedTemplate.personalization_fields.map((field, index) => (
                        <Badge key={index} variant="secondary" className="font-mono text-xs">
                          {`{{${field}}}`}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Use cases */}
              <div>
                <h4 className="font-medium mb-2">Use Cases</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedTemplate.use_cases.map((useCase, index) => (
                    <Badge key={index} variant="secondary">
                      {useCase}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={() => {
                    handleUseTemplate(selectedTemplate);
                    setPreviewOpen(false);
                  }}
                  className="flex-1"
                >
                  Use This Template
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPreviewOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmailTriggerTemplates;
